import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { get, merge } from "lodash";
import { Info } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import Tooltip from "../../Tooltip";
import useApi from "../../../../utils/api";
import { useCompany } from "../../../../utils/hooks/company";
import { preferenceQueryKeys } from "../../../../features/Settings/Configuration/api/usePreferenceQuery";
import { config } from "../../../../features/Settings/General/Notifications/Content/config";
import { renderDurationAsFormat } from "../../../../utils/helpers/date";
import { cn } from "../../../../lib/utils";

type ConfigKey = keyof typeof config;

function Notifications({ user }: any) {
    const { t } = useTranslation();
    const company = useCompany();
    const { authGet } = useApi();
    const keys = Object.keys(config) as ConfigKey[];
    const [selectedKey, setSelectedKey] = useState<ConfigKey>(keys[0]);

    const { data: _mainCompanyPreferences } = useQuery({
        queryKey: ["mainCompanyPreferences", company?.id, (company as any)?.masterCompanyId],
        queryFn: () => {
            if (!company) return null;
            const url = company.isMasterCompany
                ? `/preferencies/company/${company.id}?preferenceKey=notifications`
                : `/preferencies/company/${(company as any).masterCompanyId}?preferenceKey=notifications`;
            return authGet(url);
        },
        enabled: Boolean(company),
    });

    const { data: _companyPreferencesRaw } = useQuery({
        queryKey: preferenceQueryKeys.company(company?.id as string),
        queryFn: () => authGet(`/preferencies/company/${company?.id}?preferenceKey=notifications`),
        enabled: !!company?.id,
    });

    const { data: userPreferencesRaw, isLoading } = useQuery({
        queryKey: [...preferenceQueryKeys.user(user?.id as string), "notifications"],
        queryFn: () => authGet(`/preferencies/user/${user?.id}`),
        enabled: !!user?.id,
    });

    const mainCompanyPreferences = useMemo(() => {
        return _mainCompanyPreferences?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;
    }, [_mainCompanyPreferences]);

    const companyPreferences = useMemo(() => {
        return _companyPreferencesRaw?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;
    }, [_companyPreferencesRaw]);

    const [resolved, setResolved] = useState<any>({});

    useEffect(() => {
        const userConfig = userPreferencesRaw?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;

        const values = Object.entries(config).reduce((total: any, [key, sections]) => {
            total[key] = Object.entries(sections).reduce((_total: any, [sectionKey, items]: any) => {
                return {
                    ..._total,
                    [sectionKey]: items.reduce((acc: any, item: any) => {
                        const main = mainCompanyPreferences ? get(mainCompanyPreferences, [key, sectionKey, item.type], null) : null;
                        const comp = companyPreferences ? get(companyPreferences, [key, sectionKey, item.type], null) : null;
                        const usr = userConfig ? get(userConfig, [key, sectionKey, item.type], null) : null;

                        if (main?.force) return { ...acc, [item.type]: main };
                        if (comp?.force) return { ...acc, [item.type]: comp };

                        const defaults: any = {};
                        if ("email" in item) defaults.email = false;
                        if ("push" in item) defaults.push = false;
                        defaults.force = false;

                        return { ...acc, [item.type]: merge(defaults, usr || comp || main || {}) };
                    }, {}),
                };
            }, {});
            return total;
        }, {});

        if (!isLoading) setResolved(values);
    }, [mainCompanyPreferences, companyPreferences, userPreferencesRaw?.result, isLoading]);

    if (isLoading) {
        return (
            <div className="tw-border tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col">
                <div className="tw-flex tw-gap-4 tw-border-b tw-p-5">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="tw-h-4 tw-w-20" />)}
                </div>
                <div className="tw-flex tw-flex-col tw-divide-y">
                    {[1, 2, 3].map((s) => (
                        <div key={s} className="tw-p-5 tw-flex tw-flex-col tw-gap-3">
                            <Skeleton className="tw-h-4 tw-w-24" />
                            {[1, 2, 3].map((r) => (
                                <div key={r} className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-py-2 last:tw-border-b-0 last:tw-pb-0">
                                    <Skeleton className="tw-h-4 tw-flex-1" />
                                    <Skeleton className="tw-h-5 tw-w-14 tw-rounded-md" />
                                    <Skeleton className="tw-h-5 tw-w-14 tw-rounded-md" />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const sections = Object.entries(config[selectedKey]);

    return (
        <div className="tw-border tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex tw-border-b">
                {keys.map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedKey(key)}
                        className={cn(
                            "tw-px-5 tw-py-3 tw-text-sm tw-font-medium tw-border-b-2 tw--mb-px tw-transition-colors tw-capitalize tw-cursor-pointer",
                            selectedKey === key
                                ? "tw-border-primary tw-text-foreground"
                                : "tw-border-transparent tw-text-muted-foreground hover:tw-text-foreground hover:tw-border-muted-foreground"
                        )}
                    >
                        {t(key)}
                    </button>
                ))}
            </div>

            <div className="tw-flex tw-flex-col tw-divide-y tw-overflow-y-auto tw-flex-1 tw-min-h-0">
                {sections.map(([sectionKey, items]) => {
                    const hasDuration = (items as any[]).some((item) => item.duration);
                    const hasInfo = (items as any[]).some((item) => item.info);
                    const cols = ["minmax(0,1fr)", "60px", "60px"];
                    if (hasDuration) cols.push("80px");
                    if (hasInfo) cols.push("20px");
                    const colTemplate = cols.join(" ");

                    return (
                        <div key={sectionKey} className="tw-p-5">
                            <div className="tw-grid tw-items-center tw-mb-2" style={{ gridTemplateColumns: colTemplate }}>
                                <h6 className="tw-font-semibold tw-capitalize">{t(sectionKey)}</h6>
                                <span className="tw-text-center tw-text-xs tw-text-muted-foreground tw-capitalize">{t("email")}</span>
                                <span className="tw-text-center tw-text-xs tw-text-muted-foreground tw-capitalize">{t("push")}</span>
                                {hasDuration && (
                                    <span className="tw-text-center tw-text-xs tw-text-muted-foreground tw-capitalize">{t("duration")}</span>
                                )}
                                {hasInfo && <span />}
                            </div>

                            {(items as any[]).map((item) => {
                                const val = resolved[selectedKey]?.[sectionKey]?.[item.type] ?? {};
                                return (
                                    <div
                                        key={item.type}
                                        className="tw-grid tw-items-center tw-border-b tw-py-2 last:tw-border-b-0 last:tw-pb-0"
                                        style={{ gridTemplateColumns: colTemplate }}
                                    >
                                        <span className="tw-text-sm tw-text-muted-foreground tw-truncate tw-pr-3">{t(item.type)}</span>
                                        <div className="tw-flex tw-justify-center">
                                            {"email" in item ? (
                                                <span
                                                    className={cn(
                                                        "tw-uppercase tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded",
                                                        val.email ? "tw-bg-primary/10 tw-text-primary" : "tw-bg-muted tw-text-muted-foreground"
                                                    )}
                                                >
                                                    {val.email ? t("on") : t("off")}
                                                </span>
                                            ) : <span />}
                                        </div>
                                        <div className="tw-flex tw-justify-center">
                                            {"push" in item ? (
                                                <span
                                                    className={cn(
                                                        "tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded tw-uppercase",
                                                        val.push ? "tw-bg-primary/10 tw-text-primary" : "tw-bg-muted tw-text-muted-foreground"
                                                    )}
                                                >
                                                    {val.push ? t("on") : t("off")}
                                                </span>
                                            ) : <span />}
                                        </div>
                                        {hasDuration && (
                                            <div className="tw-flex tw-justify-center">
                                                {item.duration ? (
                                                    <span className="tw-text-xs tw-text-muted-foreground tw-font-mono">
                                                        {val.value ? renderDurationAsFormat(val.value, "HH:mm") : "--"}
                                                    </span>
                                                ) : <span />}
                                            </div>
                                        )}
                                        {hasInfo && (
                                            <div className="tw-flex tw-justify-center">
                                                {item.info ? (
                                                    <Tooltip trigger={<Info className="tw-size-3.5 tw-text-muted-foreground tw-cursor-help" />}>
                                                        {t(item.info)}
                                                    </Tooltip>
                                                ) : <span />}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default Notifications;
